import React, { memo } from 'react';
import { formats } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';

const OTHERS_KEY = 'OUTROS';
const LEGEND_MAX_HEIGHT = 100;
const DRILLDOWN_LEGEND_MAX_HEIGHT = 92;
const LEGEND_LABEL_WIDTH = 220;
const THRESHOLD_PERCENT = 0.01;
const MIN_VISIBLE_PERCENT = 0.005;

const SetoresChart = memo(props => {
  const { data = [] } = props;

  const processData = (pData, pThresholdTotal = null, pReferenceTotal = null) => {
    const xData = Object.values(pData);
    const xTotal = xData.reduce((sum, item) => sum + item.financeiro, 0);

    const xThreshold = (pThresholdTotal ?? xTotal) * THRESHOLD_PERCENT;
    const xReferenceTotal = pReferenceTotal ?? pThresholdTotal ?? xTotal;
    const xMainData = {};

    let xOthersSumFinanceiro = 0;
    let xOthersSumAtivos = 0;

    Object.entries(pData).forEach(([xKey, xItem]) => {
      const xVisiblePercent = xReferenceTotal ? (xItem.financeiro / xReferenceTotal) * 100 : 0;

      if (xItem.financeiro >= xThreshold && xVisiblePercent >= MIN_VISIBLE_PERCENT) {
        xMainData[xKey] = xItem;
      } else {
        xOthersSumFinanceiro += xItem.financeiro;
        xOthersSumAtivos += xItem.quantidade_ativos;
      }
    });

    const xOthersVisiblePercent = xReferenceTotal
      ? (xOthersSumFinanceiro / xReferenceTotal) * 100
      : 0;

    if (xOthersSumFinanceiro > 0 && xOthersVisiblePercent >= MIN_VISIBLE_PERCENT) {
      xMainData[OTHERS_KEY] = {
        financeiro: xOthersSumFinanceiro,
        quantidade_ativos: xOthersSumAtivos
      };
    }

    return xMainData;
  };

  const reduceData = (
    pData = [],
    pGroupBy = 'emissor_top_setor_id',
    pNameKey = 'top_setor',
    pEnableDrilldown = false,
    pLegendTotal = null,
    pThresholdTotal = null
  ) => {
    if (!pData.length) {
      return [];
    }

    const xLegendTotal = pLegendTotal ?? pData.reduce((xSum, xItem) => xSum + xItem.financeiro, 0);
    const xThresholdTotal = pThresholdTotal ?? xLegendTotal;

    let xGrouped = pData.reduce((xAcc, xItem) => {
      const xKey = xItem[pGroupBy];

      if (!xAcc[xKey]) {
        xAcc[xKey] = {
          ativos: [],
          financeiro: 0,
          top_setor: '',
          setor: '',
          emissor_top_setor_id: '',
          quantidade_ativos: 0
        };
      }

      xAcc[xKey].ativos.push(xItem);
      xAcc[xKey].financeiro += xItem.financeiro;
      xAcc[xKey].top_setor = xItem.top_setor;
      xAcc[xKey].setor = xItem.setor;
      xAcc[xKey].quantidade_ativos += xItem.quantidade_ativos;

      return xAcc;
    }, {});

    xGrouped = processData(xGrouped, xThresholdTotal, xLegendTotal);

    const xVisibleKeys = new Set(Object.keys(xGrouped).filter(key => key !== OTHERS_KEY));

    const xResult = Object.entries(xGrouped).map(([xKey, xValue]) => {
      const xIsOthers = xKey === OTHERS_KEY;

      return {
        name: xIsOthers ? 'Outros' : xValue[pNameKey],
        y: xValue.financeiro,
        ...(pEnableDrilldown && { drilldown: xKey }),
        metadata: {
          percentual: xLegendTotal
            ? `${formats.formatNumber((xValue.financeiro / xLegendTotal) * 100, 2, true)}%`
            : '--%',
          reference_total: xLegendTotal,
          quantidade_ativos: xValue.quantidade_ativos,
          financeiro: formats.formatNumber(xValue.financeiro, 2, true, true),
          ativos: xIsOthers
            ? pData.filter(xItem => !xVisibleKeys.has(String(xItem[pGroupBy])))
            : xValue.ativos
        }
      };
    });

    return xResult.sort((xItemA, xItemB) => {
      if (xItemA.name === 'Outros') {
        return 1;
      }
      if (xItemB.name === 'Outros') {
        return -1;
      }

      return xItemB.y - xItemA.y;
    });
  };

  const setorData = pData => {
    if (!pData.length) {
      return [];
    }

    return pData.map(xItem => {
      return {
        id: xItem.drilldown,
        name: xItem.name,
        showInLegend: true,
        type: 'pie',
        data: reduceData(
          xItem.metadata.ativos,
          'emissor_setor_id',
          'setor',
          false,
          xItem.metadata.reference_total,
          xItem.y
        )
      };
    });
  };

  const xTopSetor = reduceData(data, 'emissor_top_setor_id', 'top_setor', true);
  const xSetor = setorData(xTopSetor);

  const xOptions = {
    chart: {
      type: 'pie',
      height: '340px',
      events: {
        drilldown() {
          this.update(
            {
              legend: {
                maxHeight: DRILLDOWN_LEGEND_MAX_HEIGHT
              }
            },
            true,
            false
          );
        },
        drillupall() {
          this.update(
            {
              legend: {
                maxHeight: LEGEND_MAX_HEIGHT
              }
            },
            true,
            false
          );
        }
      }
    },
    title: false,
    legend: {
      align: 'center',
      enabled: true,
      labelFormatter: function () {
        const xTotal =
          this.series?.userOptions?.reference_total ||
          this.options?.metadata?.reference_total ||
          this.metadata?.reference_total ||
          0;
        const xPercentual = xTotal ? formats.formatNumber((this.y / xTotal) * 100, 2, true) : '--';
        const xIsVisible = this.visible !== false;
        const xNameDecoration = xIsVisible
          ? this.drilldown
            ? 'underline'
            : 'none'
          : 'line-through';
        const xPercentDecoration = xIsVisible ? 'none' : 'line-through';
        const xName = `<span style="text-decoration: ${xNameDecoration};">${this.name}</span>`;

        return `<span style="display: inline-flex; align-items: center; justify-content: space-between; gap: 8px; width: ${LEGEND_LABEL_WIDTH}px;">
          <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${xName}</span>
          <span style="flex-shrink: 0; text-align: right; text-decoration: ${xPercentDecoration};">${xPercentual}%</span>
        </span>`;
      },
      events: {
        itemClick: function (event) {
          const xPoint = event.legendItem;

          event.browserEvent?.preventDefault?.();

          if (xPoint?.drilldown) {
            setTimeout(() => {
              if (!xPoint.destroyed) {
                xPoint.doDrilldown();
              }
            }, 0);
          }

          return false;
        }
      },
      itemMarginBottom: 6,
      layout: 'vertical',
      maxHeight: LEGEND_MAX_HEIGHT,
      navigation: {
        activeColor: '#00dfa8',
        inactiveColor: '#7a81ab',
        style: {
          color: '#FFFFFF'
        }
      },
      useHTML: true,
      verticalAlign: 'bottom'
    },
    plotOptions: {
      pie: {
        innerSize: '50%',
        size: 160,
        shadow: false,
        borderColor: '#2e2e46', // Cor do contorno (borda)
        borderWidth: 2, // Largura do contorno
        dataLabels: {
          enabled: false,
          style: {
            color: '#FFFFFF',
            textOutline: 'none',
            zIndex: 1
          }
        }
      }
    },
    tooltip: {
      useHTML: true,
      style: {
        zIndex: 1000 // Set a high z-index for the tooltip
      },
      pointFormat: `Financeiro: <b>{point.metadata.financeiro}</b><br>Quantidade de Ativos: <b>{point.metadata.quantidade_ativos}</b><br>Percentual: <b>{point.metadata.percentual}</b>`
    },
    series: [
      {
        name: 'Setor',
        colorByPoint: true,
        data: xTopSetor,
        showInLegend: true
      }
    ],
    drilldown: {
      series: xSetor,
      breadcrumbs: {
        style: {
          color: '#FFFFFF',
          fill: '#FFFFFF' // Text color
        },
        buttonTheme: {
          style: {
            color: '#00dfa8' // Text color
          },
          states: {
            hover: {
              fill: 'none' // Cor de fundo ao passar o mouse
            },
            select: {
              style: {
                color: '#FFFFFF',
                cursor: 'default' // Cor do texto quando selecionado
              },
              fill: 'none'
            }
          }
        }
      }
    }
  };

  return <HighchartsReact options={xOptions} drilldown />;
});

export default SetoresChart;
